import React, { createContext, useContext, useState, useEffect } from 'react';
import { CategoryInsightsSettingsContext } from 'contexts/CategoryInsightsSettingsContext';
import { CategoryManagementContext } from 'contexts/CategoryManagementContext';
import { getContractCoveragePercentData } from 'utils/apiRequests';
import {
	chartheight,
	FONTSTYLE,
} from 'CategoryManagement/Dashboard/ChartsList/chartUtils';
import { colors } from '@alixpartners/ui-components';
import { renderToString } from 'react-dom/server';
import {
	TooltipColor,
	TooltipContainer,
	TooltipItem,
	TooltipItemName,
	TooltipItemTitle,
	TooltipItemValue,
} from './DashboardChart.styles';
import { formatSpendWithDecimalAndSymbol } from 'utils/number';
import { getRequiredCustomFilterObjectFormat } from '../../CategoryManagement/utils';

const ContractCoveragePercent = createContext();

export const ContractCoveragePercentProvider = ({
	children,
	currencySymbol,
	hideDecimals,
}) => {
	const [isLoading, setIsLoading] = useState(true);
	const [options, setOptions] = useState({
		chart: {
			type: 'column',
			style: { fontFamily: 'Roboto' },
			height: chartheight,
			zoomType: 'xy',
			inverted: true,
		},
		title: { text: '' },
		exporting: { enabled: false, allowHTML: true },
		credits: { enabled: false },
		loading: {
			hideDuration: 500,
			showDuration: 500,
			labelStyle: {
				fontWeight: 'normal',
				letterSpacing: '0.3px',
			},
		},
		xAxis: {
			title: {
				style: { ...FONTSTYLE },
			},
			labels: {
				formatter: function() {
					if (typeof this.value !== 'number') {
						if (this.value === undefined) {
							return '';
						} else {
							if (this.value.length > 0) {
								return `${this.value.substring(0, 6)}...`;
							}
						}
					}
				},
				style: {
					...FONTSTYLE,
				},
			},
			lineColor: colors.black,
			gridLineWidth: 0,
			tickInterval: 1,
		},
		yAxis: {
			title: {
				text: 'Contract Coverage',
			},
			gridLineWidth: 0,
			min: 0,
		},
		legend: { enabled: false },
		tooltip: {
			shared: true,
			useHTML: true,
			backgroundColor: colors.white,
			formatter: function() {
				return renderToString(
					<TooltipContainer>
						<TooltipItemTitle>{this.x}</TooltipItemTitle>
						{this.points.map(item => (
							<TooltipItem key={`${item.point.series.name}-${item.point.y}`}>
								<span>
									<TooltipColor color={item.color} />
									<TooltipItemName>{item.point.series.name}</TooltipItemName>
								</span>
								<TooltipItemValue>
									{formatSpendWithDecimalAndSymbol(
										item.point.y,
										currencySymbol,
										hideDecimals,
										2
									).toUpperCase()}
								</TooltipItemValue>
							</TooltipItem>
						))}
					</TooltipContainer>
				);
			},
			borderWidth: 0,
			outside: true,
			borderRadius: 2,
		},
		plotOptions: {
			column: {
				stacking: 'percent',
				dataLabels: {
					style: {
						fontSize: '8px',
					},
					enabled: true,
					formatter: function() {
						if (this.y > 0) {
							return this.percentage.toFixed(0) + '%';
						} else {
							return null;
						}
					},
				},
				pointPadding: 0,
				groupPadding: 0.1,
			},
			series: {
				borderWidth: 0,
				stacking: 'normal',
				dataLabels: { enabled: false },
				states: { hover: { enabled: false } },
				events: {
					legendItemClick: function() {
						return false;
					},
				},
			},
		},
		colors: ['#032E45', '#448EB4', '#45B9F7', '#AFE1FC'],
		series: [],
	});

	const {
		currentSelectCategory,
		dateRangeData,
		isFetching,
		isDirect,
		isSourceable,
		taxonomyType,
		customFilters,
		customFiltersValues,
	} = useContext(CategoryManagementContext);
	const { loading: isSettingsFetching } = useContext(
		CategoryInsightsSettingsContext
	);
	const { categoryName } = currentSelectCategory;
	const { dateFrom, dateTo } = dateRangeData;

	useEffect(
		() => {
			if (!isSettingsFetching && !isFetching) {
				setIsLoading(true);
				if (
					categoryName !== '' ||
					categoryName !== null ||
					categoryName !== undefined
				) {
					const payload = {
						categoryName: categoryName,
						dateFrom: dateFrom,
						dateTo: dateTo,
						isSourceable: isSourceable,
						aptaxonomy: isDirect,
						taxonomyType: taxonomyType,
					};
					if (
						Array.isArray(customFiltersValues) &&
						customFiltersValues.length > 0
					) {
						payload.customfilter = getRequiredCustomFilterObjectFormat(
							customFilters,
							customFiltersValues
						);
					}
					getContractCoveragePercentData(payload)
						.then(response => {
							if (response.succeeded) {
								if (response.data !== null && response.data.length > 0) {
									const categoriesResult = [
										...new Set(response.data.map(obj => obj.category)),
									];

									const transformed = response.data.reduce((acc, item) => {
										const categoryIndex = acc.findIndex(
											entry => entry.name === item.vendorContractName
										);
										if (categoryIndex === -1) {
											const newData = {
												name: item.vendorContractName,
												data: Array(categoriesResult.length).fill(0),
											};
											const categoryPosition = categoriesResult.indexOf(
												item.category
											);
											if (categoryPosition !== -1) {
												newData.data[categoryPosition] = item.contractSpend;
											}
											acc.push(newData);
										} else {
											const categoryPosition = categoriesResult.indexOf(
												item.category
											);
											if (categoryPosition !== -1) {
												acc[categoryIndex].data[categoryPosition] =
													item.contractSpend;
											}
										}
										return acc;
									}, []);
									const resultantData = transformed.map(item => {
										if (item.name === 'UNKNOWN') {
											return { ...item, name: 'No Contract Data' };
										}
										return item;
									});

									setOptions(prevState => ({
										...prevState,
										xAxis: {
											...prevState.xAxis,
											categories: categoriesResult,
											tickInterval: 1,
										},
										series: resultantData,
									}));
									// setData(transformed)
								} else {
									setOptions(prevState => ({
										...prevState,
										xAxis: {
											...prevState.xAxis,
											categories: [],
											tickInterval: 1,
										},
										series: [],
									}));
								}
							}
						})
						.catch(() => setIsLoading(false))
						.finally(() => setIsLoading(false));
				} else {
					setIsLoading(false);
				}
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			dateRangeData,
			categoryName,
			isSettingsFetching,
			isFetching,
			isDirect,
			isSourceable,
			taxonomyType,
			customFilters,
			customFiltersValues,
		]
	);

	return (
		<ContractCoveragePercent.Provider value={{ isLoading, options }}>
			{children}
		</ContractCoveragePercent.Provider>
	);
};

export const useContractCoveragePercentContext = () => {
	return useContext(ContractCoveragePercent);
};
